import React, { useState } from 'react';
import {
    Box, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup,
    InputAdornment, IconButton, Tooltip, MenuItem, Select, FormControl,
    InputLabel, Button, Collapse, ThemeProvider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import TuneIcon from '@mui/icons-material/Tune';
import ClearIcon from '@mui/icons-material/Clear';
import lightTheme from '../themes/lightTheme';

/**
 * Filtros compartilhados pra páginas de log (Logs Fail, Sessões).
 *
 * Props:
 *   value         { preset, date_from, date_to, username, nas_ip, status }
 *   onChange      (novoValor) => void  — chamado a cada mudança
 *   onApply       () => void  — chamado quando user pressiona "Aplicar"
 *   showStatus    bool — mostra filtro Ativo/Encerrado (radacct)
 *   showNas       bool — mostra campo NAS IP (radacct)
 */
const PRESETS = [
    { id: '24h',    label: 'Últimas 24h' },
    { id: '7d',     label: '7 dias' },
    { id: '30d',    label: '30 dias' },
    { id: 'custom', label: 'Personalizado' },
];

const DEFAULT_VALUE = {
    preset: '24h',
    date_from: '',
    date_to: '',
    username: '',
    nas_ip: '',
    status: '',
};

export default function LogFilters({ value, onChange, onApply, showStatus = false, showNas = false }) {
    const v = { ...DEFAULT_VALUE, ...(value || {}) };
    const [advanced, setAdvanced] = useState(false);

    const update = (patch) => onChange({ ...v, ...patch });

    const setPreset = (_, newPreset) => {
        if (!newPreset) return;
        // Mudar preset limpa range custom (a não ser que vire 'custom')
        if (newPreset !== 'custom') {
            update({ preset: newPreset, date_from: '', date_to: '' });
        } else {
            update({ preset: 'custom' });
        }
    };

    const clear = () => {
        onChange({ ...DEFAULT_VALUE });
        if (onApply) setTimeout(onApply, 0);
    };

    const onKeyApply = (e) => {
        if (e.key === 'Enter' && onApply) onApply();
    };

    return (
        <ThemeProvider theme={lightTheme}>
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.paper', color: 'text.primary' }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <ToggleButtonGroup
                    value={v.preset}
                    exclusive
                    size="small"
                    onChange={setPreset}
                >
                    {PRESETS.map((p) => (
                        <ToggleButton key={p.id} value={p.id}>{p.label}</ToggleButton>
                    ))}
                </ToggleButtonGroup>

                <TextField
                    size="small"
                    placeholder="Buscar usuário…"
                    value={v.username}
                    onChange={(e) => update({ username: e.target.value })}
                    onKeyDown={onKeyApply}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: 220 }}
                />

                {showStatus && (
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            label="Status"
                            value={v.status}
                            onChange={(e) => update({ status: e.target.value })}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="active">Ativas</MenuItem>
                            <MenuItem value="closed">Encerradas</MenuItem>
                        </Select>
                    </FormControl>
                )}

                <Tooltip title="Filtros avançados">
                    <IconButton size="small" onClick={() => setAdvanced((a) => !a)} color={advanced ? 'primary' : 'default'}>
                        <TuneIcon />
                    </IconButton>
                </Tooltip>

                <Box sx={{ flexGrow: 1 }} />

                <Button variant="contained" onClick={onApply} startIcon={<RefreshIcon />}>
                    Aplicar
                </Button>
                <Tooltip title="Limpar filtros">
                    <IconButton onClick={clear}>
                        <ClearIcon />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Collapse in={advanced || v.preset === 'custom'}>
                <Stack direction="row" spacing={2} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                    {v.preset === 'custom' && (
                        <>
                            <TextField
                                label="De"
                                type="datetime-local"
                                size="small"
                                value={v.date_from}
                                onChange={(e) => update({ date_from: e.target.value })}
                                onKeyDown={onKeyApply}
                                InputLabelProps={{ shrink: true }}
                                sx={{ minWidth: 220 }}
                            />
                            <TextField
                                label="Até"
                                type="datetime-local"
                                size="small"
                                value={v.date_to}
                                onChange={(e) => update({ date_to: e.target.value })}
                                onKeyDown={onKeyApply}
                                InputLabelProps={{ shrink: true }}
                                sx={{ minWidth: 220 }}
                            />
                        </>
                    )}

                    {showNas && (
                        <TextField
                            label="NAS / Equipamento (IP)"
                            size="small"
                            value={v.nas_ip}
                            onChange={(e) => update({ nas_ip: e.target.value })}
                            onKeyDown={onKeyApply}
                            sx={{ minWidth: 220 }}
                        />
                    )}
                </Stack>
            </Collapse>
        </Paper>
        </ThemeProvider>
    );
}
